import getFreshLogo from "@static/img/getfresh-gradient@2x.png";
import raviImg from "@static/img/ravi@2x.png";
import rickImg from "@static/img/rick@2x.png";
import logoDev from "@static/img/topicflow-logo-no-text.svg";
import logo from "@static/img/topicflow-logo-no-text.svg";
import { useRef, useState } from "react";
import { TbCaretDownFilled } from "react-icons/tb";
import { useLocation } from "react-router-dom";

import ButtonSSO from "@components/button-sso/button-sso";
import AppLink from "@components/link/link";
import Loading from "@components/loading/loading";
import Modal from "@components/modal/modal";
import { classNames } from "@helpers/css";
import { getAssetUrl } from "@helpers/helpers";

interface Props {
  isRegisterPage?: boolean;
}

const CreateDemoDialog = ({ onClose }: { onClose: any }) => {
  const focusRef = useRef(null);
  const [showExtraLoader, setShowExtraLoader] = useState(false);
  setTimeout(() => {
    setShowExtraLoader(true);
  }, 2000);
  return (
    <Modal
      onClose={onClose}
      initialFocus={focusRef}
      dialogClassName="md:max-w-md"
    >
      <div
        className="px-4 py-8 md:max-w-lg"
        aria-label="Set agenda as template dialog"
      >
        <div className="mt-4 flex align-middle items-center flex-col text-gray-800 text-center">
          <Loading size="8" mini className="shrink-0 inline-block mb-6" />
          <div className="font-semibold mb-4">
            Building your personal demo account
          </div>
          {showExtraLoader && (
            <div className="text-gray-500 mb-4">Just a few more seconds...</div>
          )}
        </div>
      </div>
    </Modal>
  );
};

const RegisterPageQuote = ({ version }: { version: any }) => {
  const quoteData = {
    default: {
      text: "Topicflow has quickly become our most used meeting management app. It has helped us stay on track with project action items, efficiently find previous notes, and simplify our note-taking process.",
      img: getFreshLogo,
      author: "",
      authorTitle: "",
    },
    hr: {
      text: "The search feature and the depth of integration are the two things that have made Topicflow indispensable.",
      author: "Rick Brooks-Hill",
      img: rickImg,
      authorTitle: "Co-founder @ Goldpan",
    },
    product: {
      text: "Topicflow is a way to capture action items from recurring meetings and hold team members to account for meeting responsibilities.",
      author: "Ravi Patel",
      img: raviImg,
      authorTitle: "Product Manager @ Ananda Cooperative",
    },
  };
  const quote =
    version === "1"
      ? quoteData.hr
      : version === "2"
        ? quoteData.product
        : quoteData.default;

  return (
    <div className="hidden md:flex lg:mt-0 md:max-w-[360px] lg:max-w-[500px] flex-none justify-center items-center flex-col gap-10 gradient-pale py-8 px-12 lg:py-12 lg:px-16 rounded-lg">
      <div className="relative px-3 lg:px-6 py-8 bg-white/40 rounded-lg">
        <div className="absolute -bottom-6 left-1/2 -ml-8 w-18 h-9">
          <TbCaretDownFilled className="w-18 h-9 text-white/40" />
        </div>
        <div className="gradient-basic text-transparent bg-clip-text text-center text-base lg:text-lg quote">
          {quote.text}
        </div>
      </div>
      {quote === quoteData.default ? (
        <div
          className="flex w-full h-[54px] bg-no-repeat bg-center bg-contain"
          style={{
            backgroundImage: `url(${getAssetUrl(quote.img)})`,
          }}
        />
      ) : (
        <div className="flex flex-row gap-6 items-center">
          <div
            className="flex h-16 w-16 bg-no-repeat bg-center bg-contain"
            style={{
              backgroundImage: `url(${getAssetUrl(quote.img)})`,
            }}
          />
          <div className="flex flex-col gap-1 text-sm">
            <div className="font-semibold  text-gray-700">
              <span className="">{quote.author}</span>
            </div>
            <div className="text-gray-500">
              <span className="">{quote.authorTitle}</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const Login: React.FC<Props> = ({ isRegisterPage = false }) => {
  const location = useLocation();
  const params = new URL(document.location.toString()).searchParams;
  const redirectUrl = ["/", "/login", "/logout", "/register"].includes(
    location.pathname,
  )
    ? params.get("next") || "/"
    : location.pathname;
  const ssoInviteKeyUrl = params.get("inviteKey")
    ? `&invite_key=${params.get("inviteKey")}`
    : "";
  const audience = params.get("a") || "";
  const [openCreateDemoDialog, setOpenCreateDemoDialog] = useState(false);
  const encodedRedirectUrl = encodeURIComponent(redirectUrl);

  const onClickDemo = () => {
    setOpenCreateDemoDialog(true);
    return true;
  };

  const hasEmailError =
    window.errorMessages.length > 0 &&
    window.errorMessages[0].startsWith("Your email was not recognized");

  const buttonInviteKeyUrl = params.get("inviteKey")
    ? `&inviteKey=${params.get("inviteKey")}`
    : "";
  const otherPageUrl = `${
    isRegisterPage ? "/login" : "/register"
  }?${buttonInviteKeyUrl}${
    redirectUrl === "/" ? "" : "&next=" + encodedRedirectUrl
  }`;
  return (
    <>
      {openCreateDemoDialog && (
        <CreateDemoDialog onClose={() => setOpenCreateDemoDialog(false)} />
      )}
      <div
        className={classNames(
          "min-h-full md:min-h-0 lg:mx-auto md:mt-4 lg:mt-12 mb-12 md:rounded-md  p-4 flex flex-col md:flex-row",
          isRegisterPage ? "lg:max-w-[1200px]" : "lg:w-[580px]",
        )}
      >
        <div className="p-4 pr-8 lg:p-12 text-gray-800">
          <div className="mb-6 flex justify-center">
            <AppLink
              to="/"
              className="font-light text-2xl flex items-center"
              id="sidebar-logo"
            >
              {import.meta.env.DEV ? (
                <img
                  src={getAssetUrl(logoDev)}
                  alt="Topicflow dev"
                  className="block h-11"
                />
              ) : (
                <img
                  src={getAssetUrl(logo)}
                  alt="Topicflow"
                  className="block h-11"
                />
              )}
            </AppLink>
          </div>
          <div>
            <h1 className="text-2xl font-signika flex items-center justify-center mb-7">
              {isRegisterPage
                ? "Sign up by connecting your calendar"
                : "Log in to your Topicflow account"}
            </h1>
            {isRegisterPage && (
              <>
                <div className="mt-6">
                  Topicflow connects your calendar events with a meeting space
                  where your team can take notes, plan action items, and set
                  goals in real time. And it's free!
                </div>
              </>
            )}
            <div className="bg-white p-10 rounded-md border border-[#D4DCEC]">
              <div className="flex flex-col gap-4 ">
                <ButtonSSO
                  sso="google"
                  url={`/sso/google?${ssoInviteKeyUrl}&next=${encodedRedirectUrl}&request_calendar_scope=true&request_write_scope=true`}
                  text={`${
                    isRegisterPage ? "Sign up" : "Continue"
                  } with Google`}
                  className="justify-center"
                />
                <ButtonSSO
                  sso="microsoft"
                  url={`/sso/microsoft?${ssoInviteKeyUrl}&next=${encodedRedirectUrl}&request_calendar_scope=true&request_write_scope=true`}
                  text={`${
                    isRegisterPage ? "Sign up" : "Continue"
                  } with Microsoft`}
                  className="justify-center"
                />
              </div>

              <hr className="border-t border-[#D4DCEC] my-6" />

              <div>
                <div className="text-sm text-gray-600 mb-2">
                  Single sign-on (SSO)
                </div>
                <form action="/saml/initiate/" method="post">
                  <input type="hidden" name="next" value={redirectUrl} />
                  <input
                    type="hidden"
                    name="csrfmiddlewaretoken"
                    value={window.TF.csrf}
                  />
                  <input
                    type="email"
                    name="email"
                    placeholder="Enter your email address..."
                    className={classNames(
                      "w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm",
                      hasEmailError && "border-red-500",
                      !hasEmailError && "mb-3",
                    )}
                  />
                  {hasEmailError && (
                    <div className="text-red-500 text-sm mb-2">
                      {window.errorMessages[0]}
                    </div>
                  )}
                  <button
                    type="submit"
                    className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                  >
                    Continue with SSO
                  </button>
                </form>
              </div>
            </div>
            {isRegisterPage && (
              <>
                <div className="mt-6 text-sm text-gray-500">
                  * You will be asked to allow calendar access during signup.
                </div>

                <div className="mt-6 text-gray-500">
                  Not sure yet?
                  <a
                    href={`/create-demo-account${
                      redirectUrl === "/" ? "" : "?next=" + redirectUrl
                    }`}
                    className="ml-2 font-semibold gradient-basic text-transparent bg-clip-text"
                    onClick={onClickDemo}
                  >
                    Try a live demo account
                  </a>
                </div>
              </>
            )}
            {isRegisterPage && (
              <div className="mt-6 text-gray-500">
                <AppLink to={otherPageUrl}>
                  {isRegisterPage
                    ? "Already have a Topicflow account?"
                    : "Don't have a Topicflow account?"}{" "}
                </AppLink>
                <AppLink
                  to={otherPageUrl}
                  className="text-blue-600 font-semibold hover:underline"
                >
                  {isRegisterPage ? "Log in" : "Sign up"}
                </AppLink>
              </div>
            )}
          </div>
          <div className="mt-8 text-gray-500 text-xs px-18 items-center">
            By continuing, you acknowledge that you understand and agree to the{" "}
            <a
              href="https://www.topicflow.com/privacy-policy/"
              target="_blank"
              rel="noreferrer"
              className="hover:underline font-medium"
            >
              Privacy Policy
            </a>{" "}
            and{" "}
            <a
              href="https://www.topicflow.com/terms-of-service-saas/"
              target="_blank"
              rel="noreferrer"
              className="hover:underline font-medium"
            >
              Terms of Service
            </a>
            .
          </div>
        </div>
        {isRegisterPage && <RegisterPageQuote version={audience} />}
      </div>
    </>
  );
};
export default Login;
